/* eslint react/prop-types: 0 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { lighten } from 'polished';

import {
  MEDIUM_GREY,
  DARK_GREY,
  XOS_RED,
  GREEN,
} from '../constants/theme';

const IconContainer = styled.svg.withConfig({
  shouldForwardProp: (prop) => !['disabled', 'filledColor'].includes(prop),
})`
  fill: ${(props) => (lighten(0.4, props.disabled ? MEDIUM_GREY : props.filledColor))};
  margin: 0 5px;

  &:hover {
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    fill: ${(props) => (props.disabled ? '' : props.filledColor)};
  }
`;

const DragIndicatorContainer = styled.svg.withConfig({
  shouldForwardProp: (prop) => prop !== 'hide',
})`
  fill: ${DARK_GREY};
  fill-opacity: ${(props) => (props.hide && 0)};
  margin: 0 5px;
`;

export function PlaylistIcon({ onClickHandler }) {
  return (
    <IconContainer
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      onClick={onClickHandler}
      filledColor={DARK_GREY}
    >
      <path fill="none" d="M0 0h24v24H0V0z" />
      <path d="M4 10h12v2H4zm0-4h12v2H4zm0 8h8v2H4zm10 0v6l5-3z" />
    </IconContainer>
  );
}

export function StopIcon({ onClickHandler }) {
  return (
    <IconContainer
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      onClick={onClickHandler}
      filledColor={XOS_RED}
    >
      <path d="M0 0h24v24H0z" fill="none" /><path d="M6 6h12v12H6z" />
    </IconContainer>
  );
}

export function EditIcon({ onClickHandler }) {
  return (
    <IconContainer
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      onClick={onClickHandler}
      filledColor={DARK_GREY}
    >
      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
      <path d="M0 0h24v24H0z" fill="none" />
    </IconContainer>
  );
}

export function SaveIcon({ onClickHandler }) {
  return (
    <IconContainer
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      onClick={onClickHandler}
      filledColor={GREEN}
    >
      <path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z" />
    </IconContainer>
  );
}
export function PreviewIcon({ onClickHandler }) {
  return (
    <IconContainer
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      onClick={onClickHandler}
      filledColor={DARK_GREY}
    >
      <path d="M0 0h24v24H0z" fill="none" />
      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
    </IconContainer>
  );
}

export function DeleteIcon({ onClickHandler, disabled }) {
  return (
    <IconContainer
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      onClick={onClickHandler}
      disabled={disabled}
      filledColor={XOS_RED}
    >
      <path fill="none" d="M0 0h24v24H0V0z" />
      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zm2.46-7.12l1.41-1.41L12 12.59l2.12-2.12 1.41 1.41L13.41 14l2.12 2.12-1.41 1.41L12 15.41l-2.12 2.12-1.41-1.41L10.59 14l-2.13-2.12zM15.5 4l-1-1h-5l-1 1H5v2h14V4z" />
      <path fill="none" d="M0 0h24v24H0z" />
    </IconContainer>
  );
}

export function MarkInIcon({ onClickHandler, disabled }) {
  return (
    <IconContainer
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      onClick={onClickHandler}
      disabled={disabled}
      filledColor={GREEN}
    >
      <path d="M 2,5 L22,5 L13,20 z" />
    </IconContainer>
  );
}

export function MarkOutIcon({ onClickHandler, disabled }) {
  return (
    <IconContainer
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      onClick={onClickHandler}
      disabled={disabled}
      filledColor={XOS_RED}
    >
      <path d="M 13,5 L22,20 L2,20 z" />
    </IconContainer>
  );
}

export function DragIndicatorIcon(props) {
  return (
    <DragIndicatorContainer
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      hide={props.isDisabled}
    >
      <path fill="none" d="M0 0h24v24H0V0z" />
      <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
    </DragIndicatorContainer>
  );
}

export function VirtualClipperIcon() {
  return (
    <IconContainer
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      filledColor={DARK_GREY}
    >
      <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
    </IconContainer>
  );
}

export function CancelIcon({ onClickHandler }) {
  return (
    <IconContainer
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      onClick={onClickHandler}
      filledColor={XOS_RED}
    >
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </IconContainer>
  );
}

PlaylistIcon.propTypes = {
  onClickHandler: PropTypes.func.isRequired,
};

StopIcon.propTypes = {
  onClickHandler: PropTypes.func.isRequired,
};

EditIcon.propTypes = {
  // onClickHandler: PropTypes.func.isRequired,
};

SaveIcon.propTypes = {
  // onClickHandler: PropTypes.func.isRequired,
};

PreviewIcon.propTypes = {
  onClickHandler: PropTypes.func.isRequired,
};

DeleteIcon.propTypes = {
  onClickHandler: PropTypes.func.isRequired,
};

MarkInIcon.propTypes = {
  onClickHandler: PropTypes.func.isRequired,
};

MarkOutIcon.propTypes = {
  onClickHandler: PropTypes.func.isRequired,
};

VirtualClipperIcon.propTypes = {};

CancelIcon.propTypes = {
  // onClickHandler: PropTypes.func.isRequired,
};
